import { useMemo } from 'react';
import { Avatar } from 'components/Avatar';
import { CHAT_FILE_SENT_LABEL, getChatFileKind } from 'components/Chat/constants';
import { Text } from 'components/Text';
import { commonStrings, messageStrings } from 'constants/strings';
import { useMessageStore } from '../../../store';
import type { TInquiryContact } from 'api/inquiryChat';

export type InquiryContactLastMessage = {
  channel: string;
  message: unknown;
  timetoken: string | number;
  uuid?: string;
  recalled?: boolean;
};

type InquiryContactListItemProps = {
  contact: TInquiryContact;
  unreadCount?: number;
  isActive?: boolean;
  isOpening?: boolean;
  lastMessage?: InquiryContactLastMessage;
  onSelect: () => void;
};

export function InquiryContactListItem({
  contact,
  unreadCount = 0,
  isActive = false,
  isOpening = false,
  lastMessage,
  onSelect,
}: InquiryContactListItemProps) {
  const channelId = contact.channelId;
  const { draftMessages, draftFiles } = useMessageStore();

  const [draftText, draftFile] = useMemo(
    () =>
      channelId
        ? [draftMessages?.[channelId], draftFiles?.[channelId]]
        : [undefined, undefined],
    [channelId, draftFiles, draftMessages],
  );
  const draftFileCount = draftFile?.length;
  const draftFileType = draftFile?.[0]?.type;

  const messageView = useMemo(() => {
    if (lastMessage?.recalled) {
      return messageStrings.recalledMessagePreview;
    }

    const envelope = lastMessage?.message as
      | { message?: { text?: string; media?: unknown[] }; file?: unknown }
      | { text?: string; media?: unknown[] }
      | undefined;

    const parsed =
      envelope && typeof envelope === 'object' && 'message' in envelope
        ? envelope.message
        : envelope;

    const text =
      parsed && typeof parsed === 'object' && 'text' in parsed
        ? (parsed.text as string | undefined)
        : undefined;

    const hasFile = Boolean(
      envelope &&
      typeof envelope === 'object' &&
      'file' in envelope &&
      envelope.file,
    );
    const firstMedia =
      parsed &&
      typeof parsed === 'object' &&
      'media' in parsed &&
      Array.isArray(parsed.media)
        ? (parsed.media[0] as { type?: string } | undefined)
        : undefined;

    // hasFile（PubNub ファイル）は常に画像。media がある場合のみ型で種別（PDF/動画/ZIP）を判定する
    const file =
      hasFile || firstMedia
        ? CHAT_FILE_SENT_LABEL[getChatFileKind(firstMedia?.type)]
        : undefined;
    const message = text || file || '';

    const draftMessage =
      draftText?.message ||
      (draftFileCount
        ? CHAT_FILE_SENT_LABEL[getChatFileKind(draftFileType)]
        : undefined) ||
      message;

    return unreadCount > 0 ? message : draftMessage;
  }, [
    draftFileCount,
    // 添付を画像→PDFに差し替えたときもラベルを更新するため、種別も依存に含める
    draftFileType,
    draftText?.message,
    lastMessage,
    unreadCount,
  ]);

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={isOpening}
      className={`flex w-full items-center gap-2 border-b border-neutral-200 p-4 text-left cursor-pointer transition
         disabled:cursor-wait disabled:opacity-60 ${
           isActive ? 'bg-blue-50' : 'hover:bg-gray-50'
         }`}
    >
      <div className="place-self-center flex-shrink-0">
        <Avatar
          url={undefined}
          size="h-14 w-14"
          className="rounded-xl border border-neutral-200"
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="line-clamp-1 break-all">
          <Text className="inline" bold>
            {contact.name} ({contact.memberId})
          </Text>
        </div>
        <div className="line-clamp-1 break-all text-neutral-500 text-xs">
          {contact.organizationName}
        </div>

        <div className="flex mt-2 justify-between items-center">
          <div className="flex-1 line-clamp-1 break-all text-neutral-600 text-xs min-w-0">
            {(draftText || draftFile) && !unreadCount && (
              <span className="text-primary">{commonStrings.draft}: </span>
            )}
            {messageView}
          </div>

          {unreadCount > 0 && (
            <div className="relative w-5 h-5 bg-red-500 rounded-full ml-2 flex-shrink-0">
              <Text
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                textColor="text-white"
                textSize="text-[11px]"
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </Text>
            </div>
          )}
        </div>
      </div>
    </button>
  );
}
